import ApplicationContainer from 'application/ApplicationContainer'
import Modules from 'application/modules'
import NotFound from 'components/NotFound'
import { getModules } from 'contexts/ACL/service'
import Home from 'modules/home'
import LoginHandler from 'modules/login'
import Anonymizer from 'modules/management/anonymizer'
import Bootstrap from 'modules/management/bootstrap'
import EsnMapping from 'modules/management/esn-mapping'
import IdMapping from 'modules/management/id-mapping'
import CollisionIdMapping from 'modules/management/id-mapping/collision'
import EditableIdMapping from 'modules/management/id-mapping/editable-mapping'
import IpGeolocation from 'modules/management/ip-geolocation'
import OnnetOffnet from 'modules/management/onnet-offnet'
import AdvancedSearch from 'modules/operational/device/advanced-search'
import DeviceDetail from 'modules/operational/device/detail'
import Npvr from 'modules/operational/npvr'
import RTF from 'modules/operational/rtf'
import { createBrowserRouter, RouteObject } from 'react-router-dom'

type RouteMap = {
  path?: string
  Component?: React.ComponentType,
  index?: boolean
  children?: Array<RouteMap>
  group?: Modules
  name?: string
  key?: string
}

const publicRoutes: Array<RouteObject> = [
  {
    path: '/login',
    Component: LoginHandler,
  },
]

const operationalRoutes = () => [
  {
    path: 'device',
    children: [
      {
        index: true,
        name: 'advancedSearch',
        group: Modules.devices,
        Component: AdvancedSearch,
      },
      {
        path: ':id/:type/:hhid/:serialNr',
        name: 'deviceDetail',
        group: Modules.devices,
        Component: DeviceDetail,
      }
    ],
  },
  {
    path: 'rtf',
    children: [
      {
        index: true,
        Component: RTF,
        name: 'RTF',
        group: Modules.rtf,
      },
    ],
  },
  {
    path: 'npvr',
    children: [
      {
        index: true,
        Component: Npvr,
        name: 'NPVR',
        group: Modules.devices,
      },
      {
        path: ':hhid',
        name: 'npvrResults',
        group: Modules.devices,
        Component: Npvr,
      }
    ],
  },
] as Array<RouteMap>

const managementRoutes = () => [
  {
    path: 'id-mapping',
    children: [
      {
        index: true,
        Component: IdMapping,
        name: 'IDMapping',
        group: Modules.idMapping,
      },
      {
        path: ':id/collision',
        Component: CollisionIdMapping,
        name: 'collisionResolution',
        group: Modules.idMapping,
      },
      {
        path: 'editable-mapping',
        Component: EditableIdMapping,
        name: 'editableIdMapping',
        group: Modules.idMapping,
      },
    ],
  },

  {
    path: 'ip-geolocation',
    children: [
      {
        index: true,
        Component: IpGeolocation,
        name: 'ipGeolocation',
        group: Modules.ipGeolocation,
      },
    ]
  },

  {
    path: 'on-net-off-net',
    children: [
      {
        index: true,
        Component: OnnetOffnet,
        name: 'onnetOffnet',
        group: Modules.onNetOffNet,
      },
    ],
  },

  {
    path: 'esn-mapping',
    children: [
      {
        index: true,
        Component: EsnMapping,
        name: 'ESNMapping',
        group: Modules.esnMapping,
      },
    ],
  },

  {
    path: 'alexa-capability',
    children: [
      {
        index: true,
        Component: IdMapping,
        name: 'alexa',
        group: Modules.alexaCapability,
      },
    ],
  },

  {
    path: 'anonymizer',
    children: [
      {
        index: true,
        Component: Anonymizer,
        name: 'Anonymizer',
        group: Modules.anonymizer,
        key: Modules.anonymizer
      },
    ],
  },
  {
    path: 'bootstrap',
    children: [
      {
        index: true,
        Component: Bootstrap,
        name: 'Bootstrap',
        group: Modules.bootstrap,
      },
    ],
  },
] as Array<RouteMap>

const privateRoutes = () => [
  {
    path: '/',
    Component: ApplicationContainer,
    children: [
      {
        index: true,
        Component: Home,
        name: 'home',
      },

      {
        path: 'operational',
        children: operationalRoutes()
      },

      {
        path: 'management',
        children: managementRoutes(),
      }
    ]
  },
] as Array<RouteMap>

const notFroundRoute: Array<RouteObject> = [{
  path: '*',
  Component: NotFound,
}]

function recursiveMapping(routeMap: RouteMap) {
  delete routeMap.group
  delete routeMap.name

  if (routeMap.children) {
    routeMap.children.map(recursiveMapping)
  }

  return routeMap as RouteObject
}

function recursiveFiltering(routes: Array<RouteMap>, currentRoute: RouteMap, modules: Array<string>) {
  const childrenFilteredRoutes: Array<RouteMap> = []

  if (currentRoute.group) {
    if (modules.includes(currentRoute.group)) {
      return routes.push(currentRoute)
    }
  }

  if (currentRoute.children) {
    currentRoute.children.forEach(childRoute => recursiveFiltering(childrenFilteredRoutes, childRoute, modules))

    if (childrenFilteredRoutes.length) {
      currentRoute.children = childrenFilteredRoutes
      return routes.push(currentRoute)
    }
  }

  if (!currentRoute.group && !currentRoute.children) {
    return routes.push(currentRoute)
  }
}

function filterRoutes(routes: Array<RouteMap>, currentUserModules: Array<string>) {
  const filteredRoutes: Array<RouteMap> = []
  routes.forEach(route => recursiveFiltering(filteredRoutes, route, currentUserModules))

  return filteredRoutes
}

export default function getRouter() {
  const currentUserModules = getModules().ACL.map(ACLGroup => ACLGroup.moduleName)

  return createBrowserRouter(publicRoutes.concat(filterRoutes(privateRoutes(), currentUserModules).map((route) => recursiveMapping(route)).concat(notFroundRoute)))
}
